import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  HandCoins, Euro, Clock, CheckCircle, Calendar, Download,
  TrendingUp, ArrowUpRight, Wallet
} from 'lucide-react';
import { cn } from '../../lib/utils';

const AffiliateCommissionsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const stats = user?.affiliate_stats || {
    total_earned: 2340,
    pending_commission: 450,
    converted_referrals: 8
  };

  // Demo commissions data
  const commissions = [
    { id: 1, client: 'TechStart SAS', project: 'Refonte site web', amount: 800, status: 'paid', date: '2024-12-15', paidDate: '2024-12-20' },
    { id: 2, client: 'Design Studio', project: 'Application mobile', amount: 450, status: 'pending', date: '2024-12-10', paidDate: null },
    { id: 3, client: 'WebAgency Pro', project: 'E-commerce', amount: 600, status: 'paid', date: '2024-11-28', paidDate: '2024-12-05' },
    { id: 4, client: 'StartupXYZ', project: 'Branding complet', amount: 350, status: 'paid', date: '2024-11-15', paidDate: '2024-11-22' },
  ];

  const statusConfig = {
    'pending': { color: 'bg-amber-100 text-amber-700', label: 'En attente', icon: Clock },
    'paid': { color: 'bg-emerald-100 text-emerald-700', label: 'Payée', icon: CheckCircle },
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>Mes Commissions</h1>
          <p className="text-slate-500">Suivez vos gains et paiements</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-green-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total gagné</p>
                  <p className="text-3xl font-bold">{stats.total_earned.toLocaleString('fr-FR')}€</p>
                </div>
                <Euro className="w-10 h-10 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">En attente</p>
                  <p className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>{stats.pending_commission}€</p>
                </div>
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Clients convertis</p>
                  <p className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>{stats.converted_referrals}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Convert to Budget Card */}
        <Card className="mb-6 border-0 shadow-sm border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wallet className="w-6 h-6 text-purple-500" />
                <div>
                  <p className="font-medium" style={{ color: theme.colors?.primary }}>Convertir en budget communication</p>
                  <p className="text-sm text-slate-500">Tarif préférentiel : 90€ HT/heure (au lieu de 120€)</p>
                </div>
              </div>
              <Button variant="outline" className="border-purple-500 text-purple-600 hover:bg-purple-50">
                <ArrowUpRight className="w-4 h-4 mr-1" />
                Convertir
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Commissions List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Historique des commissions</CardTitle>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1" /> Exporter
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {commissions.map((commission) => {
                const config = statusConfig[commission.status];
                const StatusIcon = config.icon;
                return (
                  <div key={commission.id} className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-4">
                      <div className={cn("p-2 rounded-full", commission.status === 'paid' ? 'bg-emerald-100' : 'bg-amber-100')}>
                        <StatusIcon className={cn("w-5 h-5", commission.status === 'paid' ? 'text-emerald-600' : 'text-amber-600')} />
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: theme.colors?.primary }}>{commission.client}</p>
                        <p className="text-sm text-slate-500">{commission.project}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg" style={{ color: theme.colors?.primary }}>+{commission.amount}€</p>
                        <p className="text-xs text-slate-400">
                          {commission.status === 'paid' ? `Payée le ${commission.paidDate}` : `Créée le ${commission.date}`}
                        </p>
                      </div>
                      <Badge className={config.color}>{config.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AffiliateCommissionsPage;